import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem,
  TextField, IconButton, Typography, Dialog, DialogTitle, Checkbox,
  ListItemText, DialogContent, DialogContentText, DialogActions, Toolbar,
  Divider
} from '@mui/material';
import {
  FaDownload, FaPiggyBank, FaFilter, FaNotEqual, FaFileUpload,
  FaTrashAlt, FaEdit, FaUserEdit, FaFilePdf, FaBolt
} from 'react-icons/fa';
import api from '../Api';
import { openPdfWindow } from '../utils/pdfWindow';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ScanSummary from "./ScanSummary";
import NotScannedDialog from "./NotScannedDialog";
import {
  addDaysToDateString,
  formatLocalDateTime,
  formatServerDateTime,
  getAppDateString,
  getAppTimeString,
} from "../utils/date";
import { groupForAccount } from "../utils/scanGroups";

const ORDERS_SELECTED_HEADERS_KEY = "ordersPage.selectedHeaders.v1";
const ORDERS_COLUMN_WIDTHS_KEY = "ordersPage.columnWidths.v1";
const DEFAULT_SELECTED_HEADERS = [
  "order_id",
  "sku",
  "quantity",
  "scanned_on",
  "tracking_id",
  "account_name",
];

const CLEAR_ALL = "__ALL_ACCOUNTS__";
const CLEAR_GROUP_PREFIX = "__GROUP__";

const accountNameOf = (account) => String(account?.name ?? account ?? "").trim();

const clearGroupMatches = (accountName, group) => {
  const accountGroup = groupForAccount(accountName);
  return group === "MY"
    ? accountGroup === "MY" || accountGroup === "MYS" || accountGroup === "MYE"
    : accountGroup === group;
};

const getClearOptions = (accounts) => {
  const names = accounts.map(accountNameOf).filter(Boolean);
  const exactGroups = [...new Set(names.map(groupForAccount))].sort((a, b) => a.localeCompare(b));
  const groups = exactGroups.some((group) => ["MY", "MYS", "MYE"].includes(group))
    ? ["MY", ...exactGroups.filter((group) => group !== "MY")]
    : exactGroups;
  return { names, groups };
};

const readJsonStorage = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

const isDateLikeOrderColumn = (column) => (
  /(^date$|_date$|date_|_at$|^at$|time$|_time$|timestamp|ordered_on|uploaded_at|scanned_on)/i
    .test(String(column || ""))
);

const parseOrderDateMs = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  const isoMs = Date.parse(raw);
  if (!Number.isNaN(isoMs)) return isoMs;

  const match = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (!match) return null;
  const [, dd, mm, yyyy, hh = "0", min = "0", ss = "0"] = match;
  const date = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    Number(hh),
    Number(min),
    Number(ss)
  );
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  // red remaining counts open the not-scanned list (as on the dashboard)
  const [nsOpen, setNsOpen] = useState(false);
  const [nsGroup, setNsGroup] = useState("");
  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('tracking_id');
  const [filters, setFilters] = useState({});
  const [tableHeaders, setTableHeaders] = useState([]);
  const [sortColumn, setSortColumn] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedUploadAccount, setSelectedUploadAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedClearAccounts, setSelectedClearAccounts] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [clearAccountDialogOpen, setClearAccountDialogOpen] = useState(false);

  // Upload date+time state — default to now
  const getNowLocal = () => {
    const now = new Date();
    const date = getAppDateString(now);
    const time = getAppTimeString(now); // HH:mm
    return { date, time };
  };
  const [uploadDate, setUploadDate] = useState(() => getNowLocal().date);
  // ADD after:
  const [uploadTime, setUploadTime] = useState(() => getNowLocal().time);

  // ADD:
  const [orderedOnDate, setOrderedOnDate] = useState(() => getNowLocal().date);
  const [orderedOnTime, setOrderedOnTime] = useState(() => getNowLocal().time);

  const [selectedDate, setSelectedDate] = useState(getAppDateString());
  const [clearAccountDate, setClearAccountDate] = useState("");

  const [selectedHeaders, setSelectedHeaders] = useState(() => {
    return readJsonStorage(ORDERS_SELECTED_HEADERS_KEY, DEFAULT_SELECTED_HEADERS);
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    return readJsonStorage(ORDERS_COLUMN_WIDTHS_KEY, {});
  });

  // ── Bulk selection state ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditOrderedOnOpen, setBulkEditOrderedOnOpen] = useState(false);
  const [bulkEditAccountOpen, setBulkEditAccountOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkOrderedOnDate, setBulkOrderedOnDate] = useState(() => getNowLocal().date);
  const [bulkOrderedOnTime, setBulkOrderedOnTime] = useState(() => getNowLocal().time);
  const [bulkNewAccount, setBulkNewAccount] = useState("");
  const [directScanOpen, setDirectScanOpen] = useState(false);
  const [directScanRemarks, setDirectScanRemarks] = useState("");
  const [directScanning, setDirectScanning] = useState(false);

  // ── Duplicate preview state ──
  const [duplicatePreviewOpen, setDuplicatePreviewOpen] = useState(false);
  const [duplicateList, setDuplicateList] = useState([]);
  const [pendingFormData, setPendingFormData] = useState(null);

  const normalizeLongNumber = (val) => {
    if (val === null || val === undefined) return null;
    let s = String(val).trim().replace(/^'+/, "");

    const m = s.match(/^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
    if (!m) return s;

    const digits = m[2] + (m[3] || "");
    const exp = parseInt(m[4], 10);
    const dotPos = m[2].length + exp;

    if (dotPos >= digits.length) {
      return m[1] + digits + "0".repeat(dotPos - digits.length);
    }
    if (dotPos <= 0) {
      return m[1] + "0." + "0".repeat(-dotPos) + digits;
    }
    return m[1] + digits.slice(0, dotPos) + "." + digits.slice(dotPos);
  };

  const normalizeCsvHeader = (value) =>
    String(value || "")
      .replace(/^\uFEFF/, "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getCsvValue = (row, headers) => {
    const lookup = Object.keys(row || {}).reduce((acc, key) => {
      acc[normalizeCsvHeader(key)] = key;
      return acc;
    }, {});
    for (const header of headers) {
      const key = lookup[normalizeCsvHeader(header)];
      if (key && row[key] !== undefined && row[key] !== null) return row[key];
    }
    return "";
  };

  const extractTrackingIdsFromCsv = async (file) => {
    const text = await file.text();
    const parsed = Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
    });
    if (parsed.errors?.length) {
      console.warn("⚠️ CSV preview parse warnings:", parsed.errors.slice(0, 3));
    }
    return (parsed.data || [])
      .map(row => getCsvValue(row, ["Tracking_id", "Tracking ID", "Tracking Id", "AWB", "AWB No", "AWB Number"]))
      .map(normalizeLongNumber)
      .map(value => String(value || "").replace(/"/g, "").trim())
      .filter(Boolean);
  };

  const fetchOrdersByDate = async (date) => {
    const start = performance.now();
    try {
      const response = await api.get(`/api/orders-by-date/${date}`);
      const end = performance.now();
      const duration = ((end - start) / 1000).toFixed(2);
      setOrders(
        response.data.map(o => ({
          ...o,
          tracking_id: normalizeLongNumber(o.tracking_id),
          order_id: normalizeLongNumber(o.order_id),
          order_item_id: normalizeLongNumber(o.order_item_id),
          shipment_id: normalizeLongNumber(o.shipment_id),
        }))
      );
      const totalEntries = response.data.length;
      const uniqueOrders = new Set(response.data.map(o => o.tracking_id)).size;
      toast.success(`✅ Loaded ${totalEntries} entries; ${uniqueOrders} orders in ${duration}s`);
      setSelectedIds(new Set()); // clear selection on date change
    } catch (error) {
      console.error("❌ Error fetching orders by date:", error);
      toast.error("❌ Failed to fetch date-specific orders");
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/api/accounts?active_only=true");
      setAccounts(response.data);
    } catch (error) {
      console.error("❌ Error fetching accounts:", error);
    }
  };

  const fetchTableHeaders = async () => {
    try {
      const response = await api.get('/api/orders-headers');
      setTableHeaders(response.data);
      const validHeaders = new Set(response.data);
      setSelectedHeaders((current) => {
        const cleaned = (Array.isArray(current) && current.length ? current : DEFAULT_SELECTED_HEADERS)
          .filter((header) => validHeaders.has(header));
        const next = cleaned.length ? cleaned : DEFAULT_SELECTED_HEADERS.filter((header) => validHeaders.has(header));
        localStorage.setItem(ORDERS_SELECTED_HEADERS_KEY, JSON.stringify(next));
        return next;
      });
    } catch (error) {
      console.error('❌ Error fetching table headers:', error);
    }
  };

  // ── Clear account data ──
  const handleClearAccountData = async () => {
    if (!selectedClearAccounts.length) {
      alert("❌ Please select at least one account or group to clear data.");
      return;
    }
    const dateToUse = clearAccountDate || selectedDate;
    const { names } = getClearOptions(accounts);
    const accountNames = selectedClearAccounts.includes(CLEAR_ALL)
      ? names
      : [...new Set(selectedClearAccounts.flatMap((selection) => {
          if (selection.startsWith(CLEAR_GROUP_PREFIX)) {
            const group = selection.slice(CLEAR_GROUP_PREFIX.length);
            return names.filter((name) => clearGroupMatches(name, group));
          }
          return [selection];
        }))];
    if (!accountNames.length) {
      alert("❌ The selected group has no accounts.");
      return;
    }
    const selectionLabel = selectedClearAccounts.includes(CLEAR_ALL)
      ? "ALL accounts"
      : accountNames.length === 1
        ? accountNames[0]
        : `${accountNames.length} accounts`;
    const confirmMsg = `⚠️ WARNING:\nThis will remove ALL orders for ${selectionLabel} on ${dateToUse}.\nProceed?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      // Use POST to avoid axios DELETE body issues
      await api.post("/api/clear-account-data", {
        accountNames,
        date: dateToUse
      });
      toast.success(`🧹 Deleted records for ${selectionLabel} on ${dateToUse}`);
      setClearAccountDialogOpen(false);
      setSelectedClearAccounts([]);
      setClearAccountDate("");
      fetchOrdersByDate(dateToUse);
    } catch (error) {
      console.error("❌ Error clearing account data:", error);
      toast.error("❌ Failed to clear account data.");
    }
  };

  // ── Upload: check duplicates then upload ──
  const handleUploadClick = async () => {
    if (!selectedFile || !selectedUploadAccount || !uploadDate || !uploadTime) {
      alert("❌ Please select an account, date, time, and a file.");
      return;
    }

    // Build ordered_on datetime string: "DD-MM-YYYY HH:mm"
    const [oy, om, od] = orderedOnDate.split("-");
    const orderedOnFormatted = `${od}-${om}-${oy} ${orderedOnTime}`;
    // ISO for uploadedAt
    const uploadedAtISO = `${uploadDate}T${uploadTime}:00`;

    const formData = new FormData();
    formData.append("csvFile", selectedFile);
    formData.append("accountName", selectedUploadAccount);
    formData.append("uploadedAt", uploadedAtISO);
    formData.append("orderedOn", orderedOnFormatted);
    formData.append("uploadDate", uploadDate); // for duplicate scoping

    // Read tracking IDs from CSV to pre-check duplicates
    try {
      const trackingIds = await extractTrackingIdsFromCsv(selectedFile);

      const dupRes = await api.post("/api/orders/check-duplicates", {
        tracking_ids: trackingIds,
        date: uploadDate
      });

      const dupes = dupRes.data?.duplicates || [];
      if (dupes.length > 0) {
        setDuplicateList(dupes);
        setPendingFormData(formData);
        setDuplicatePreviewOpen(true);
        return;
      }
    } catch (err) {
      console.warn("⚠️ Duplicate check failed, proceeding anyway:", err.message);
    }

    // No duplicates — upload directly
    await submitUpload(formData);
  };

  const submitUpload = async (formData) => {
    try {
      const response = await api.post("/api/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert(`✅ ${response.data.message}`);
      window.location.reload(true);
    } catch (error) {
      console.error("❌ Upload Error:", error);
      const message = error.response?.data?.message || error.response?.data?.error || "❌ Upload failed!";
      alert(message);
    }
  };

  const filterUniqueTrackingIds = async () => {
    const start = performance.now();
    try {
      const response = await api.get('/api/unique-orders', { params: { date: selectedDate } });
      const end = performance.now();
      const duration = ((end - start) / 1000).toFixed(2);
      const allEntries = response.data;
      const uniqueOrders = new Set(allEntries.map(e => e.tracking_id)).size;
      setOrders(allEntries);
      toast.success(`✅ Showing unscanned ${allEntries.length} entries; ${uniqueOrders} orders in ${duration}s`);
    } catch (error) {
      console.error('❌ Error fetching unique orders:', error);
      toast.error('❌ Failed to fetch unscanned orders for selected date');
    }
  };

  const downloadCSV = () => {
    if (filteredOrders.length === 0) {
      alert('❌ No orders available to download.');
      return;
    }
    const csvHeader = selectedHeaders.map(h => h.replace(/_/g, ' ').toUpperCase());
    const csvRows = filteredOrders.map(order =>
      selectedHeaders.map(header => {
        const value = order[header] ?? 'N/A';
        const cell = typeof value === "number"
          ? value.toLocaleString("fullwide", { useGrouping: false })
          : String(value).replace(/"/g, '""');
        return cell.includes(',') || cell.includes('"') ? `"${cell}"` : cell;
      })
    );
    const csvContent = [csvHeader.join(','), ...csvRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'filtered_orders.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("✅ CSV downloaded successfully!");
  };

  // ── Bulk selection helpers ──
  const allVisibleIds = sortedOrdersIds(); // forward ref, defined after sortedOrders
  const allSelected = sortedOrders => sortedOrders.length > 0 && sortedOrders.every(o => selectedIds.has(o.id));
  const someSelected = sortedOrders => sortedOrders.some(o => selectedIds.has(o.id));

  const toggleSelectAll = (sortedOrders) => {
    if (allSelected(sortedOrders)) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedOrders.map(o => o.id)));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Bulk delete ──
  const handleBulkDelete = async () => {
    try {
      await api.post("/api/orders/bulk-delete", { ids: [...selectedIds] });
      toast.success(`🗑️ Deleted ${selectedIds.size} rows`);
      setBulkDeleteConfirmOpen(false);
      setSelectedIds(new Set());
      fetchOrdersByDate(selectedDate);
    } catch (err) {
      console.error("❌ Bulk delete failed:", err);
      toast.error("❌ Bulk delete failed");
    }
  };

  // ── Open the label PDFs for the selected rows (from the server archive) ──
  const [openingLabels, setOpeningLabels] = useState(false);
  const openSelectedLabels = async () => {
    const pickedRows = orders.filter(o => selectedIds.has(o.id) && o.tracking_id);
    const ids = pickedRows.map(o => o.tracking_id).filter(Boolean);
    if (!ids.length) { toast.warn("No tracking IDs in the selected rows."); return; }
    const preview = openPdfWindow();
    setOpeningLabels(true);
    try {
      const resp = await api.post("/api/label-pdf",
        {
          tracking_ids: [...new Set(ids)],
          date: selectedDate,
          items: pickedRows.map((row) => ({
            id: row.tracking_id,
            tracking_id: row.tracking_id,
            account: row.account_name || "",
            order_id: row.order_id || "",
            order_item_id: row.order_item_id || "",
            sku: row.sku || "",
          })),
        },
        { responseType: "blob" });
      preview.show(new Blob([resp.data], { type: "application/pdf" }), "selected_labels.pdf");
    } catch (err) {
      preview.fail();
      let msg = "Failed to open labels.";
      try {
        const payload = JSON.parse(await err.response.data.text());
        msg = payload.error || msg;
        if (Array.isArray(payload.fallback) && payload.fallback.length && !/fallback/i.test(msg)) {
          const detail = payload.fallback
            .map((row) => {
              const platform = row.platform || "fallback";
              if (!row.attempted) return `${platform} fallback skipped: ${row.reason || "not applicable"}`;
              const count = row.printed ?? row.recovered ?? row.linked ?? 0;
              return `${platform} fallback ${row.ok ? `ok (${count})` : `failed${row.error ? `: ${row.error}` : ""}`}`;
            })
            .join("; ");
          if (detail) msg += ` ${detail}`;
        }
        if (Array.isArray(payload.missing_ids) && payload.missing_ids.length) {
          msg += ` Missing: ${payload.missing_ids.join(", ")}`;
        }
      } catch (_) {}
      toast.error("❌ " + msg);
    } finally {
      setOpeningLabels(false);
    }
  };

  const openDirectScan = () => {
    const pickedRows = orders.filter(o => selectedIds.has(o.id) && o.tracking_id && o.account_name);
    if (!pickedRows.length) {
      toast.warn("No valid tracking IDs in the selected rows.");
      return;
    }
    setDirectScanRemarks("");
    setDirectScanOpen(true);
  };

  const confirmDirectScan = async () => {
    const scans = orders
      .filter(o => selectedIds.has(o.id) && o.tracking_id && o.account_name)
      .map(o => ({ tracking_id: o.tracking_id, account_name: o.account_name }));
    if (!scans.length) return;
    setDirectScanning(true);
    try {
      const response = await api.post("/api/admin/direct-scan-selected", {
        date: selectedDate,
        scans,
        remarks: directScanRemarks,
      });
      setDirectScanOpen(false);
      const remarkNote = response.data?.remarks_saved ? ` Remark saved for ${response.data.remarks_saved} account(s).` : "";
      toast.success(`✅ ${response.data?.message || "Direct scan completed."}${remarkNote}`);
      setSelectedIds(new Set());
      fetchOrdersByDate(selectedDate);
    } catch (err) {
      toast.error("❌ " + (err.response?.data?.message || err.response?.data?.error || "Direct scan failed."));
    } finally {
      setDirectScanning(false);
    }
  };

  // ── Bulk edit ordered_on ──
  const handleBulkEditOrderedOn = async () => {
    const [year, month, day] = bulkOrderedOnDate.split("-");
    const formatted = `${day}-${month}-${year} ${bulkOrderedOnTime}`;
    try {
      await api.post("/api/orders/bulk-update-ordered-on", {
        ids: [...selectedIds],
        ordered_on: formatted
      });
      toast.success(`✅ Updated ordered_on for ${selectedIds.size} rows`);
      setBulkEditOrderedOnOpen(false);
      setSelectedIds(new Set());
      fetchOrdersByDate(selectedDate);
    } catch (err) {
      console.error("❌ Bulk edit ordered_on failed:", err);
      toast.error("❌ Failed to update ordered_on");
    }
  };

  // ── Bulk edit account ──
  const handleBulkEditAccount = async () => {
    if (!bulkNewAccount) { alert("❌ Please select an account."); return; }
    try {
      await api.post("/api/orders/bulk-update-account", {
        ids: [...selectedIds],
        account_name: bulkNewAccount
      });
      toast.success(`✅ Updated account for ${selectedIds.size} rows`);
      setBulkEditAccountOpen(false);
      setSelectedIds(new Set());
      fetchOrdersByDate(selectedDate);
    } catch (err) {
      console.error("❌ Bulk edit account failed:", err);
      toast.error("❌ Failed to update account");
    }
  };

  const handleSort = (column) => {
    const newDirection = (sortColumn === column && sortDirection === 'asc') ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const handleColumnResize = (header, newWidth) => {
    setColumnWidths(prev => {
      const updated = { ...prev, [header]: newWidth };
      localStorage.setItem(ORDERS_COLUMN_WIDTHS_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({ ...prev, [column]: value.toLowerCase() }));
  };

  const filteredOrders = orders.filter(order => {
    if (searchQuery) {
      const value = order[searchColumn]?.toString().toLowerCase();
      if (!value?.includes(searchQuery.toLowerCase())) return false;
    }
    return Object.keys(filters).every(column =>
      order[column]?.toString().toLowerCase().includes(filters[column] || '')
    );
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    const av = a[sortColumn];
    const bv = b[sortColumn];

    if (isDateLikeOrderColumn(sortColumn)) {
      const ad = parseOrderDateMs(av);
      const bd = parseOrderDateMs(bv);
      if (ad !== null || bd !== null) {
        if (ad === null) return 1;
        if (bd === null) return -1;
        return (ad - bd) * dir;
      }
    }

    const an = Number(av);
    const bn = Number(bv);
    if (Number.isFinite(an) && Number.isFinite(bn)) {
      return (an - bn) * dir;
    }

    return String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * dir;
  });

  function sortedOrdersIds() { return []; } // placeholder, used inline below

  const handleHeaderSelection = (newSelectedHeaders) => {
    setSelectedHeaders(newSelectedHeaders);
    localStorage.setItem(ORDERS_SELECTED_HEADERS_KEY, JSON.stringify(newSelectedHeaders));
  };

  const resetHeaderSelection = () => {
    const validHeaders = new Set(tableHeaders);
    const next = DEFAULT_SELECTED_HEADERS.filter((header) => validHeaders.has(header));
    setSelectedHeaders(next);
    localStorage.setItem(ORDERS_SELECTED_HEADERS_KEY, JSON.stringify(next));
    toast.info("Default columns restored");
  };

  useEffect(() => {
    fetchTableHeaders();
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchOrdersByDate(selectedDate);
  }, [selectedDate]);

  // ✅ Re-fetch orders when tab becomes visible again (e.g. after rename on AccountsPage)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchOrdersByDate(selectedDate);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [selectedDate]);

  const selectionCount = selectedIds.size;

  return (
    <Box sx={{ padding: '10px' }}>

      {/* ── Top bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', fontFamily: `'Caveat', 'Patrick Hand', 'Comic Sans MS', cursive`, flexShrink: 0, fontStyle: 'normal' }}>
          🛒 Orders
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" startIcon={<FaDownload />} onClick={downloadCSV}>CSV</Button>
          <Button variant="contained" color="secondary" startIcon={<FaFilter />} onClick={() => { setFilters({}); fetchOrdersByDate(selectedDate); toast.info("✅ Filters cleared"); }}>CLR</Button>
          <Button variant="contained" color="primary" startIcon={<FaNotEqual />} onClick={filterUniqueTrackingIds}>SCAN</Button>
          <Button variant="contained" color="secondary" startIcon={<FaPiggyBank />} onClick={() => setClearAccountDialogOpen(true)}>CLR</Button>
          <Button variant="contained" color="primary" onClick={() => setUploadDialogOpen(true)} startIcon={<FaFileUpload />}>CSV</Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ width: 100 }}>
            <InputLabel>Columns</InputLabel>
            <Select multiple value={selectedHeaders} onChange={(e) => handleHeaderSelection(e.target.value)} renderValue={() => null}>
              {tableHeaders.map((header) => (
                <MenuItem key={header} value={header}>
                  <Checkbox checked={selectedHeaders.includes(header)} />
                  <ListItemText primary={header.replace(/_/g, " ").toUpperCase()} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={resetHeaderSelection}>
            Default
          </Button>
        </Box>
      </Box>

      {/* ── Search + count + date nav ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>Search In</InputLabel>
            <Select value={searchColumn} onChange={(e) => setSearchColumn(e.target.value)} label="Search In">
              <MenuItem value="account_name">Account Name</MenuItem>
              <MenuItem value="tracking_id">Tracking ID</MenuItem>
              <MenuItem value="sku">SKU</MenuItem>
              <MenuItem value="quantity">Quantity</MenuItem>
              <MenuItem value="order_id">Order ID</MenuItem>
              <MenuItem value="scanned_on">Scanned On</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} size="small" sx={{ width: 300 }} />
        </Box>

        <Box sx={{ fontWeight: 'bold', fontSize: '20px', color: '#333' }}>
          Showing: {filteredOrders.length} Entries; {new Set(filteredOrders.map(o => o.tracking_id)).size} Orders
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => setSelectedDate(addDaysToDateString(selectedDate, -1))}>
            <ChevronLeftIcon sx={{ fontSize: 20, color: 'black' }} />
          </IconButton>
          <TextField type="date" size="small" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
          <IconButton size="small" onClick={() => setSelectedDate(addDaysToDateString(selectedDate, 1))}>
            <ChevronRightIcon sx={{ fontSize: 20, color: 'black' }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Bulk action toolbar (visible when rows selected) ── */}
      {selectionCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>{selectionCount} selected</Typography>
          <Button size="small" variant="contained" color="info" startIcon={<FaFilePdf />} disabled={openingLabels} onClick={openSelectedLabels}>{openingLabels ? "Opening…" : "Open Labels"}</Button>
          <Button size="small" variant="contained" color="warning" startIcon={<FaBolt />} disabled={directScanning} onClick={openDirectScan}>{directScanning ? "Scanning…" : "Scan Direct"}</Button>
          <Button size="small" variant="contained" color="error" startIcon={<FaTrashAlt />} onClick={() => setBulkDeleteConfirmOpen(true)}>Delete</Button>
          <Button size="small" variant="contained" color="warning" startIcon={<FaEdit />} onClick={() => { const n = getNowLocal(); setBulkOrderedOnDate(n.date); setBulkOrderedOnTime(n.time); setBulkEditOrderedOnOpen(true); }}>Edit Date</Button>
          <Button size="small" variant="contained" color="info" startIcon={<FaUserEdit />} onClick={() => { setBulkNewAccount(""); setBulkEditAccountOpen(true); }}>Edit Account</Button>
          <Button size="small" variant="outlined" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </Box>
      )}

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Select All checkbox */}
              <TableCell
                style={{ width: 48, backgroundColor: '#666', padding: '4px 8px' }}
                sx={{ position: 'sticky', left: 0, zIndex: 3 }}
              >
                <Checkbox
                  size="small"
                  sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                  checked={sortedOrders.length > 0 && sortedOrders.every(o => selectedIds.has(o.id))}
                  indeterminate={sortedOrders.some(o => selectedIds.has(o.id)) && !sortedOrders.every(o => selectedIds.has(o.id))}
                  onChange={() => {
                    if (sortedOrders.every(o => selectedIds.has(o.id))) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(new Set(sortedOrders.map(o => o.id)));
                    }
                  }}
                />
              </TableCell>

              {tableHeaders.filter(h => selectedHeaders.includes(h)).map((header) => (
                <TableCell
                  key={header}
                  style={{ width: columnWidths[header] || '150px', cursor: 'col-resize', fontWeight: 'bold', backgroundColor: '#666', color: 'white' }}
                  onMouseDown={(e) => {
                    const startX = e.clientX;
                    const startWidth = e.target.offsetWidth;
                    const handleMouseMove = (e) => handleColumnResize(header, startWidth + (e.clientX - startX));
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{header.replace(/_/g, ' ').toUpperCase()}</span>
                    <IconButton size="small" onClick={() => handleSort(header)} style={{ color: 'white' }}>
                      {sortColumn === header ? (sortDirection === 'asc' ? '🔼' : '🔽') : '⇅'}
                    </IconButton>
                  </div>
                  <TextField size="small" placeholder={`Filter ${header}`} onChange={(e) => handleFilterChange(header, e.target.value)} fullWidth style={{ marginTop: 4 }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedOrders.map((order, index) => (
              <TableRow
                key={order.id ?? index}
                sx={{ backgroundColor: selectedIds.has(order.id) ? '#bbdefb' : index % 2 === 0 ? '#f9f9f9' : '#e0e0e0' }}
              >
                {/* Row checkbox */}
                <TableCell sx={{ padding: '0 8px' }}>
                  <Checkbox size="small" checked={selectedIds.has(order.id)} onChange={() => toggleRow(order.id)} />
                </TableCell>

                {tableHeaders.filter(h => selectedHeaders.includes(h)).map((header) => (
                  <TableCell key={header}>
                    {header === "scanned_on"
                      ? formatServerDateTime(order[header])
                      : header === "uploaded_at" || header === "ordered_on"
                      ? formatLocalDateTime(order[header])
                      : order[header] ?? "N/A"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Clear Account Dialog ── */}
      <Dialog open={clearAccountDialogOpen} onClose={() => setClearAccountDialogOpen(false)}>
        <DialogTitle>Clear Data for Account</DialogTitle>
        <DialogContent>
          <DialogContentText>Select one or more accounts, a group, or ALL accounts and a date to clear its data:</DialogContentText>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Account</InputLabel>
            <Select
              multiple
              value={selectedClearAccounts}
              label="Select Account"
              onChange={(e) => {
                const next = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                const hasAll = next.includes(CLEAR_ALL);
                const hadAll = selectedClearAccounts.includes(CLEAR_ALL);
                if (hasAll && !hadAll) {
                  setSelectedClearAccounts([CLEAR_ALL]);
                } else if (hasAll && next.length > 1) {
                  setSelectedClearAccounts(next.filter((value) => value !== CLEAR_ALL));
                } else {
                  setSelectedClearAccounts(next);
                }
              }}
              renderValue={(selected) => {
                if (!selected.length) return "Select account(s)";
                if (selected.includes(CLEAR_ALL)) return "ALL accounts";
                const groupCount = selected.filter((value) => value.startsWith(CLEAR_GROUP_PREFIX)).length;
                const accountCount = selected.length - groupCount;
                return `${groupCount ? `${groupCount} group${groupCount > 1 ? "s" : ""}` : ""}${groupCount && accountCount ? " + " : ""}${accountCount ? `${accountCount} account${accountCount > 1 ? "s" : ""}` : ""}`;
              }}
            >
              <MenuItem value={CLEAR_ALL}>
                <Checkbox checked={selectedClearAccounts.includes(CLEAR_ALL)} />
                <ListItemText primary="ALL accounts" />
              </MenuItem>
              <Divider />
              {getClearOptions(accounts).groups.map((group) => {
                const value = `${CLEAR_GROUP_PREFIX}${group}`;
                return (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={selectedClearAccounts.includes(value)} />
                    <ListItemText primary={`${group} group`} />
                  </MenuItem>
                );
              })}
              {getClearOptions(accounts).groups.length > 0 && <Divider />}
              {getClearOptions(accounts).names.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={selectedClearAccounts.includes(name)} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Clear Date" type="date" fullWidth value={clearAccountDate} onChange={(e) => setClearAccountDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ marginTop: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAccountDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleClearAccountData} color="error">Clear Data</Button>
        </DialogActions>
      </Dialog>

      {/* ── Upload CSV Dialog ── */}
      <Dialog open={uploadDialogOpen} onClose={() => {  setUploadDialogOpen(false);  const n = getNowLocal();  setUploadDate(n.date); setUploadTime(n.time);  setOrderedOnDate(n.date); setOrderedOnTime(n.time);  setSelectedFile(null);}}>
        <DialogTitle>Upload CSV File</DialogTitle>
        <DialogContent>
          <DialogContentText>Select account, date & time, then file. The date+time will be stored as <b>ordered_on</b> for all entries.</DialogContentText>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Account</InputLabel>
            <Select value={selectedUploadAccount} onChange={(e) => setSelectedUploadAccount(e.target.value)}>
              <MenuItem value="">-- Select Account --</MenuItem>
              {accounts.map(account => (
                <MenuItem key={account.id} value={account.name}>{account.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
            📅 Uploaded At — controls which date this file appears under in the table
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <TextField label="Upload Date" type="date" fullWidth value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
            <TextField label="Upload Time" type="time" fullWidth value={uploadTime} onChange={(e) => setUploadTime(e.target.value)} InputLabelProps={{ shrink: true }} required />
          </Box>
          <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#666' }}>
            🗓️ Ordered On — stored in the ordered_on column for each entry (can differ from upload date)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <TextField label="Ordered On Date" type="date" fullWidth value={orderedOnDate} onChange={(e) => setOrderedOnDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
            <TextField label="Ordered On Time" type="time" fullWidth value={orderedOnTime} onChange={(e) => setOrderedOnTime(e.target.value)} InputLabelProps={{ shrink: true }} required />
          </Box>
          <input type="file" accept=".csv" onChange={(e) => setSelectedFile(e.target.files[0])} style={{ marginTop: "16px" }} />
            <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
            <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', display: 'block' }}>
              ⚠️ DO NOT open the CSV in Excel before uploading.
            </Typography>
            <Typography variant="caption" sx={{ color: '#bf360c', display: 'block' }}>
              Excel destroys long numeric IDs. Upload directly as downloaded from Flipkart/Myntra portal. If must use Excel: Data → Get Data → From Text/CSV → set ID columns as Text.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleUploadClick} color="secondary">Upload</Button>
        </DialogActions>
      </Dialog>

      {/* ── Duplicate Preview Dialog ── */}
      <Dialog open={duplicatePreviewOpen} onClose={() => setDuplicatePreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>⚠️ Duplicates Found ({duplicateList.length})</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            These tracking IDs already exist on <b>{uploadDate}</b>. Proceeding will add duplicates. Cancel to abort.
          </DialogContentText>
          <Box sx={{ maxHeight: 200, overflowY: 'auto', backgroundColor: '#f9f9f9', p: 1, borderRadius: 1 }}>
            {duplicateList.map((tid, i) => (
              <Typography key={i} variant="body2">{tid}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDuplicatePreviewOpen(false); setPendingFormData(null); }} color="primary">Cancel</Button>
          <Button onClick={async () => { setDuplicatePreviewOpen(false); await submitUpload(pendingFormData); }} color="error">Upload Anyway</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Delete Confirm ── */}
      <Dialog open={bulkDeleteConfirmOpen} onClose={() => setBulkDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>Delete <b>{selectionCount}</b> selected rows permanently?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Edit Ordered On ── */}
      <Dialog open={bulkEditOrderedOnOpen} onClose={() => setBulkEditOrderedOnOpen(false)}>
        <DialogTitle>Edit Ordered On — {selectionCount} rows</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>Set new date & time for all selected rows. Format stored: DD-MM-YYYY HH:mm</DialogContentText>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField label="Date" type="date" fullWidth value={bulkOrderedOnDate} onChange={(e) => setBulkOrderedOnDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Time" type="time" fullWidth value={bulkOrderedOnTime} onChange={(e) => setBulkOrderedOnTime(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOrderedOnOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkEditOrderedOn} color="primary">Update</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Edit Account ── */}
      <Dialog open={bulkEditAccountOpen} onClose={() => setBulkEditAccountOpen(false)}>
        <DialogTitle>Change Account — {selectionCount} rows</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>New Account</InputLabel>
            <Select value={bulkNewAccount} onChange={(e) => setBulkNewAccount(e.target.value)}>
              <MenuItem value="">-- Select Account --</MenuItem>
              {accounts.map(a => <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditAccountOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkEditAccount} color="primary">Update</Button>
        </DialogActions>
      </Dialog>

      <ScanSummary selectedDate={selectedDate} setSelectedDate={setSelectedDate} readOnlyDate={true} accounts={accounts}
        onOpenNotScanned={(group) => { setNsGroup(group || ""); setNsOpen(true); }} />
      {nsOpen && (
        <NotScannedDialog open={nsOpen} onClose={() => setNsOpen(false)}
          accounts={accounts} initialGroup={nsGroup} />
      )}
      <Dialog open={directScanOpen} onClose={() => !directScanning && setDirectScanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scan Selected Orders Directly</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Scan {selectionCount} selected order{selectionCount === 1 ? "" : "s"} for {selectedDate}?
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Remarks (optional)"
            value={directScanRemarks}
            onChange={(e) => setDirectScanRemarks(e.target.value)}
            placeholder="Add a note for this account/date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDirectScanOpen(false)} disabled={directScanning}>Cancel</Button>
          <Button onClick={confirmDirectScan} variant="contained" color="warning" disabled={directScanning}>
            {directScanning ? "Scanning…" : "Confirm Scan"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersPage;
